import React, { Component } from "react";
import Helmet from "react-helmet";
import axios from "axios";
import GridLoader from "react-spinners/GridLoader";
import Layout from "../../Layout/layout";
import { apiHost } from "../../api/apiClient";
import "../../styles/maintenance/maintenanceEdit.scss";

const DATE_THRESHOLD_KEY = "maintenance_due_date_threshold_days";
const KM_THRESHOLD_KEY = "maintenance_due_km_threshold";

const getValue = (obj, key) => {
  if (!obj) return null;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[pascal];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatKm = (value, decimals = 1) => {
  const number = toNumber(value);
  if (number === null) return "-";
  return number.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `$${number.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
};

const dateValue = (value) => {
  if (!value || value === "0000-00-00") return "";
  return String(value).slice(0, 10);
};

const todayValue = () => {
  const today = new Date();
  const offset = today.getTimezoneOffset() * 60000;
  return new Date(today.getTime() - offset).toISOString().slice(0, 10);
};

const emptyCommentForm = () => ({
  date: todayValue(),
  comment: "",
  cost: "",
  parts: [],
});

const emptyPartForm = (installedDate) => ({
  partName: "",
  partNumber: "",
  supplier: "",
  invoiceNumber: "",
  quantity: "",
  unitCost: "",
  installedDate: installedDate || todayValue(),
  warrantyMonths: "",
  warrantyExpiry: "",
  notes: "",
});

const parseDueDate = (value) => {
  if (!value || value === "0000-00-00") return null;
  const parts = String(value).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getWarrantyStatus = (expiryDate) => {
  if (!expiryDate) return { label: "No expiry", className: "active" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(`${expiryDate}T00:00:00`);
  const daysRemaining = Math.ceil((expiry.getTime() - today.getTime()) / 86400000);
  if (daysRemaining < 0) return { label: "Expired", className: "expired" };
  if (daysRemaining <= 60) return { label: `Expiring: ${daysRemaining} days`, className: "expiring" };
  return { label: "Active", className: "active" };
};

class MaintenanceEdit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      asset: null,
      form: {
        registrationDue: "",
        insuranceDue: "",
        roadworthyDue: "",
        serviceDueDate: "",
        serviceDueKm: "",
      },
      isLoading: false,
      isSaving: false,
      comments: [],
      commentsLoading: false,
      commentSearch: "",
      commentModalOpen: false,
      editingCommentId: null,
      commentForm: emptyCommentForm(),
      commentError: "",
      isCommentSaving: false,
      partsCatalogueOpen: false,
      partsCatalogueSearch: "",
      warrantyReportOpen: false,
      warrantyReportSearch: "",
      warrantyReportStatus: "all",
      errorMessage: "",
      saveMessage: "",
      dateThresholdDays: this.getStoredNumber(DATE_THRESHOLD_KEY, 7),
      kmThreshold: this.getStoredNumber(KM_THRESHOLD_KEY, 2000),
    };
  }

  componentDidMount() {
    this.loadAsset();
    this.loadComments();
  }

  getStoredNumber = (key, fallback) => {
    const parsed = Number(localStorage.getItem(key));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };

  getAssetId = () => this.props.match && this.props.match.params ? this.props.match.params.assetId : "";

  loadAsset = async () => {
    const assetId = this.getAssetId();
    this.setState({ isLoading: true, errorMessage: "", saveMessage: "" });
    try {
      const response = await axios.get(`${apiHost}/api/Assets/MaintenanceDue/${assetId}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      this.setAssetState(response.data || {});
    } catch (e) {
      const message = e && e.response && e.response.data && e.response.data.message
        ? e.response.data.message
        : String(e);
      this.setState({ isLoading: false, errorMessage: message });
    }
  };

  loadComments = async () => {
    const assetId = this.getAssetId();
    this.setState({ commentsLoading: true });
    try {
      const response = await axios.get(`${apiHost}/api/Assets/MaintenanceDue/${assetId}/Comments`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      this.setState({ comments: response.data || [], commentsLoading: false });
    } catch (e) {
      this.setState({ comments: [], commentsLoading: false });
    }
  };

  setAssetState = (asset) => {
    this.setState({
      asset,
      form: {
        registrationDue: dateValue(getValue(asset, "registrationDue")),
        insuranceDue: dateValue(getValue(asset, "insuranceDue")),
        roadworthyDue: dateValue(getValue(asset, "roadworthyDue")),
        serviceDueDate: dateValue(getValue(asset, "serviceDueDate")),
        serviceDueKm: getValue(asset, "serviceDueKm") && Number(getValue(asset, "serviceDueKm")) > 0
          ? String(getValue(asset, "serviceDueKm"))
          : "",
      },
      isLoading: false,
    });
  };

  handleField = (name, value) => {
    this.setState((state) => ({
      form: {
        ...state.form,
        [name]: value,
      },
      saveMessage: "",
    }));
  };

  save = async (event) => {
    event.preventDefault();
    const assetId = this.getAssetId();
    const { form } = this.state;
    const serviceDueKm = Number(form.serviceDueKm);
    const payload = {
      registrationDue: form.registrationDue || null,
      insuranceDue: form.insuranceDue || null,
      roadworthyDue: form.roadworthyDue || null,
      serviceDueDate: form.serviceDueDate || null,
      serviceDueKm: getValue(this.state.asset, "isTracked") !== false && Number.isFinite(serviceDueKm) && serviceDueKm > 0
        ? serviceDueKm
        : null,
    };

    this.setState({ isSaving: true, errorMessage: "", saveMessage: "" });
    try {
      const response = await axios.put(`${apiHost}/api/Assets/MaintenanceDue/${assetId}`, payload, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      this.setAssetState(response.data || {});
      this.setState({ isSaving: false, saveMessage: "Changes saved successfully." });
    } catch (e) {
      const message = e && e.response && e.response.data && e.response.data.message
        ? e.response.data.message
        : String(e);
      this.setState({ isSaving: false, errorMessage: message });
    }
  };

  goBack = () => {
    const query = this.props.location && this.props.location.search ? this.props.location.search : "";
    this.props.history.push(`/maintenance-due${query}`);
  };

  today = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  dateStatus = (value) => {
    const date = parseDueDate(value);
    if (!date) return "";
    const days = (date.getTime() - this.today().getTime()) / 86400000;
    if (days <= 0) return "overdue";
    if (days <= this.state.dateThresholdDays) return "upcoming";
    return "";
  };

  kmStatus = (dueKm, odometerKm) => {
    const due = toNumber(dueKm);
    const odometer = toNumber(odometerKm);
    if (due === null || due <= 0 || odometer === null) return "";
    const remaining = due - odometer;
    if (remaining <= 0) return "overdue";
    if (remaining <= this.state.kmThreshold) return "upcoming";
    return "";
  };

  fieldClass = (status) => `maintenance-edit-field${status ? ` ${status}` : ""}`;

  renderStatusPill = (status) => {
    if (!status) return null;
    return <span className={`maintenance-edit-status-pill ${status}`}>{status === "overdue" ? "Overdue" : "Soon"}</span>;
  };

  renderDateField = (name, label) => {
    const status = this.dateStatus(this.state.form[name]);
    return (
      <label className={this.fieldClass(status)}>
        <span className="maintenance-edit-label-row">
          <span>{label}</span>
          {this.renderStatusPill(status)}
        </span>
        <input
          type="date"
          value={this.state.form[name]}
          onChange={(event) => this.handleField(name, event.target.value)}
        />
      </label>
    );
  };

  openNewComment = () => {
    this.setState({
      commentModalOpen: true,
      editingCommentId: null,
      commentForm: emptyCommentForm(),
      commentError: "",
      saveMessage: "",
    });
  };

  openEditComment = (comment) => {
    const parts = (getValue(comment, "parts") || []).map((part) => ({
      partName: getValue(part, "partName") || "",
      partNumber: getValue(part, "partNumber") || "",
      supplier: getValue(part, "supplier") || "",
      invoiceNumber: getValue(part, "invoiceNumber") || "",
      quantity: getValue(part, "quantity") || "",
      unitCost: getValue(part, "unitCost") || "",
      installedDate: dateValue(getValue(part, "installedDate")) || dateValue(getValue(comment, "date")) || todayValue(),
      warrantyMonths: getValue(part, "warrantyMonths") || "",
      warrantyExpiry: dateValue(getValue(part, "warrantyExpiry")),
      notes: getValue(part, "notes") || "",
    }));

    this.setState({
      commentModalOpen: true,
      editingCommentId: getValue(comment, "id"),
      commentForm: {
        date: dateValue(getValue(comment, "date")) || todayValue(),
        comment: getValue(comment, "comment") || "",
        cost: getValue(comment, "cost") || "",
        parts,
      },
      commentError: "",
      saveMessage: "",
    });
  };

  closeCommentModal = () => {
    if (this.state.isCommentSaving) return;
    this.setState({ commentModalOpen: false, commentError: "" });
  };

  handleCommentField = (name, value) => {
    this.setState((state) => ({
      commentForm: {
        ...state.commentForm,
        [name]: value,
      },
      commentError: "",
    }));
  };

  addPartRow = () => {
    this.setState((state) => ({
      commentForm: {
        ...state.commentForm,
        parts: [
          ...state.commentForm.parts,
          emptyPartForm(state.commentForm.date),
        ],
      },
    }));
  };

  removePartRow = (index) => {
    this.setState((state) => ({
      commentForm: {
        ...state.commentForm,
        parts: state.commentForm.parts.filter((_, partIndex) => partIndex !== index),
      },
    }));
  };

  updatePartRow = (index, name, value) => {
    this.setState((state) => {
      const parts = state.commentForm.parts.map((part, partIndex) => {
        if (partIndex !== index) return part;
        const next = { ...part, [name]: value };
        if (name === "installedDate" || name === "warrantyMonths") {
          next.warrantyExpiry = this.calculateWarrantyExpiry(next.installedDate, next.warrantyMonths) || next.warrantyExpiry;
        }
        return next;
      });

      return {
        commentForm: {
          ...state.commentForm,
          parts,
        },
        commentError: "",
      };
    });
  };

  calculateWarrantyExpiry = (installedDate, warrantyMonths) => {
    const months = Number(warrantyMonths);
    if (!installedDate || !Number.isFinite(months) || months <= 0) return "";
    const expiry = new Date(`${installedDate}T00:00:00`);
    expiry.setMonth(expiry.getMonth() + months);
    if (Number.isNaN(expiry.getTime())) return "";
    return expiry.toISOString().slice(0, 10);
  };

  getCommentPayload = () => {
    const { commentForm } = this.state;
    const cost = Number(commentForm.cost);
    return {
      date: commentForm.date || null,
      comment: commentForm.comment || "",
      cost: Number.isFinite(cost) && cost >= 0 ? cost : null,
      parts: commentForm.parts
        .filter((part) => String(part.partName || "").trim())
        .map((part) => {
          const quantity = Number(part.quantity);
          const unitCost = Number(part.unitCost);
          const warrantyMonths = Number(part.warrantyMonths);
          return {
            partName: part.partName,
            partNumber: part.partNumber || null,
            supplier: part.supplier || null,
            invoiceNumber: part.invoiceNumber || null,
            quantity: Number.isFinite(quantity) && quantity >= 0 ? quantity : null,
            unitCost: Number.isFinite(unitCost) && unitCost >= 0 ? unitCost : null,
            installedDate: part.installedDate || null,
            warrantyMonths: Number.isFinite(warrantyMonths) && warrantyMonths >= 0 ? warrantyMonths : null,
            warrantyExpiry: part.warrantyExpiry || null,
            notes: part.notes || null,
          };
        }),
    };
  };

  saveComment = async (event) => {
    event.preventDefault();
    const assetId = this.getAssetId();
    const payload = this.getCommentPayload();
    const headers = { Authorization: "Bearer " + localStorage.getItem("token") };
    const isEditing = !!this.state.editingCommentId;
    const url = isEditing
      ? `${apiHost}/api/Assets/MaintenanceDue/${assetId}/Comments/${this.state.editingCommentId}`
      : `${apiHost}/api/Assets/MaintenanceDue/${assetId}/Comments`;

    this.setState({ isCommentSaving: true, commentError: "", errorMessage: "", saveMessage: "" });
    try {
      const response = isEditing
        ? await axios.put(url, payload, { headers })
        : await axios.post(url, payload, { headers });
      this.setState({
        comments: response.data || [],
        commentModalOpen: false,
        editingCommentId: null,
        commentForm: emptyCommentForm(),
        isCommentSaving: false,
        saveMessage: isEditing ? "Maintenance log updated successfully." : "Maintenance log added successfully.",
      });
    } catch (e) {
      const message = e && e.response && e.response.data && e.response.data.message
        ? e.response.data.message
        : String(e);
      this.setState({ isCommentSaving: false, commentError: message });
    }
  };

  deleteComment = async () => {
    if (!this.state.editingCommentId || this.state.isCommentSaving) return;
    if (!window.confirm("Are you sure you want to delete this maintenance log entry? This cannot be undone.")) return;

    const assetId = this.getAssetId();
    this.setState({ isCommentSaving: true, commentError: "", errorMessage: "", saveMessage: "" });
    try {
      const response = await axios.delete(`${apiHost}/api/Assets/MaintenanceDue/${assetId}/Comments/${this.state.editingCommentId}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      this.setState({
        comments: response.data || [],
        commentModalOpen: false,
        editingCommentId: null,
        commentForm: emptyCommentForm(),
        isCommentSaving: false,
        saveMessage: "Maintenance log deleted successfully.",
      });
    } catch (e) {
      const message = e && e.response && e.response.data && e.response.data.message
        ? e.response.data.message
        : String(e);
      this.setState({ isCommentSaving: false, commentError: message });
    }
  };

  getFilteredComments = () => {
    const search = this.state.commentSearch.trim().toLowerCase();
    if (!search) return this.state.comments;

    return this.state.comments.filter((comment) => {
      const partsText = (getValue(comment, "parts") || []).map((part) => [
        getValue(part, "partName"),
        getValue(part, "partNumber"),
        getValue(part, "supplier"),
        getValue(part, "invoiceNumber"),
        getValue(part, "installedDate"),
        getValue(part, "warrantyExpiry"),
        getWarrantyStatus(getValue(part, "warrantyExpiry")).label,
        getValue(part, "notes"),
      ].join(" ")).join(" ");

      return [
        getValue(comment, "date"),
        getValue(comment, "comment"),
        getValue(comment, "cost"),
        getValue(comment, "updatedBy") || getValue(comment, "createdBy"),
        formatDateTime(getValue(comment, "updatedAt") || getValue(comment, "createdAt")),
        partsText,
      ].join(" ").toLowerCase().indexOf(search) !== -1;
    });
  };

  getLoggedParts = () => this.state.comments.reduce((parts, comment) => {
    const commentParts = getValue(comment, "parts") || [];
    commentParts.forEach((part, index) => {
      const warrantyExpiry = dateValue(getValue(part, "warrantyExpiry"));
      const warranty = getWarrantyStatus(warrantyExpiry);
      const quantity = Number(getValue(part, "quantity"));
      const unitCost = Number(getValue(part, "unitCost"));
      parts.push({
        id: getValue(part, "id") || `${getValue(comment, "id") || "comment"}-${index}`,
        commentId: getValue(comment, "id"),
        logDate: dateValue(getValue(comment, "date")),
        logEntry: getValue(comment, "comment") || "",
        partName: getValue(part, "partName") || "",
        partNumber: getValue(part, "partNumber") || "",
        supplier: getValue(part, "supplier") || "",
        invoiceNumber: getValue(part, "invoiceNumber") || "",
        quantity: Number.isFinite(quantity) ? quantity : null,
        unitCost: Number.isFinite(unitCost) ? unitCost : null,
        installedDate: dateValue(getValue(part, "installedDate")),
        warrantyMonths: getValue(part, "warrantyMonths"),
        warrantyExpiry,
        warranty,
        notes: getValue(part, "notes") || "",
      });
    });
    return parts;
  }, []);

  getPartsCatalogue = () => {
    const catalogue = new Map();

    this.getLoggedParts().forEach((part) => {
      const key = [
        String(part.partName || "").trim().toLowerCase(),
        String(part.partNumber || "").trim().toLowerCase(),
        String(part.supplier || "").trim().toLowerCase(),
      ].join("|");
      const existing = catalogue.get(key);
      const installedTime = part.installedDate ? new Date(`${part.installedDate}T00:00:00`).getTime() : 0;
      const existingTime = existing && existing.lastInstalledDate ? new Date(`${existing.lastInstalledDate}T00:00:00`).getTime() : 0;

      if (!existing) {
        catalogue.set(key, {
          ...part,
          defaultWarrantyMonths: part.warrantyMonths,
          lastInstalledDate: part.installedDate,
          lastUnitCost: part.unitCost,
          totalQuantity: part.quantity || 0,
          usageCount: 1,
        });
        return;
      }

      catalogue.set(key, {
        ...existing,
        defaultWarrantyMonths: existing.defaultWarrantyMonths || part.warrantyMonths,
        lastInstalledDate: installedTime >= existingTime ? part.installedDate : existing.lastInstalledDate,
        lastUnitCost: installedTime >= existingTime && part.unitCost !== null ? part.unitCost : existing.lastUnitCost,
        notes: existing.notes || part.notes,
        totalQuantity: existing.totalQuantity + (part.quantity || 0),
        usageCount: existing.usageCount + 1,
      });
    });

    return Array.from(catalogue.values()).sort((a, b) => (a.partName || "").localeCompare(b.partName || ""));
  };

  matchesText = (row, search, fields) => {
    if (!search) return true;
    return fields.map((field) => row[field]).join(" ").toLowerCase().indexOf(search) !== -1;
  };

  getFilteredPartsCatalogue = () => {
    const search = this.state.partsCatalogueSearch.trim().toLowerCase();
    return this.getPartsCatalogue().filter((part) => this.matchesText(part, search, [
      "partName",
      "partNumber",
      "supplier",
      "invoiceNumber",
      "notes",
    ]));
  };

  getFilteredWarrantyParts = () => {
    const search = this.state.warrantyReportSearch.trim().toLowerCase();
    const status = this.state.warrantyReportStatus;
    return this.getLoggedParts()
      .filter((part) => {
        let statusMatch = status === "all";
        if (status === "no-expiry") statusMatch = !part.warrantyExpiry;
        if (status === "active") statusMatch = !!part.warrantyExpiry && part.warranty.className === "active";
        if (status === "expiring" || status === "expired") statusMatch = part.warranty.className === status;
        return statusMatch && this.matchesText(part, search, [
          "partName",
          "partNumber",
          "supplier",
          "invoiceNumber",
          "installedDate",
          "warrantyExpiry",
          "logDate",
          "logEntry",
          "notes",
        ]);
      })
      .sort((a, b) => String(a.warrantyExpiry || "9999-12-31").localeCompare(String(b.warrantyExpiry || "9999-12-31")));
  };

  renderParts = (parts) => {
    if (!parts || !parts.length) return null;

    return (
      <div className="maintenance-parts-summary">
        <strong>Parts Used</strong>
        <table>
          <thead>
            <tr>
              <th>Part</th>
              <th>Qty / Cost</th>
              <th>Installed</th>
              <th>Warranty Ends</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((part) => {
              const warranty = getWarrantyStatus(getValue(part, "warrantyExpiry"));
              const quantity = Number(getValue(part, "quantity"));
              const unitCost = Number(getValue(part, "unitCost"));
              return (
                <tr key={getValue(part, "id")}>
                  <td>
                    <strong>{getValue(part, "partName") || "-"}</strong>
                    {getValue(part, "partNumber") && <span>{getValue(part, "partNumber")}</span>}
                    {getValue(part, "supplier") && <span>{getValue(part, "supplier")}</span>}
                    {getValue(part, "invoiceNumber") && <span>Invoice: {getValue(part, "invoiceNumber")}</span>}
                  </td>
                  <td>
                    {Number.isFinite(quantity) ? `Qty ${quantity}` : "-"}
                    {Number.isFinite(unitCost) && <> / {formatCurrency(unitCost)}</>}
                  </td>
                  <td>{getValue(part, "installedDate") || "-"}</td>
                  <td>{getValue(part, "warrantyExpiry") || "-"}</td>
                  <td><span className={`maintenance-warranty-pill ${warranty.className}`}>{warranty.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  renderComments = () => {
    const comments = this.getFilteredComments();
    if (this.state.commentsLoading) {
      return <div className="maintenance-comments-empty">Loading maintenance log...</div>;
    }

    if (!this.state.comments.length) {
      return <div className="maintenance-comments-empty">No logs currently available for this asset.</div>;
    }

    if (!comments.length) {
      return <div className="maintenance-comments-empty">No matching logs found.</div>;
    }

    return (
      <div className="maintenance-comment-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Log Entry</th>
              <th>Cost</th>
              <th>Last Updated By</th>
              <th>Last Updated At</th>
              <th>Edit</th>
            </tr>
          </thead>
          <tbody>
            {comments.map((comment) => (
              <tr key={getValue(comment, "id")}>
                <td>{getValue(comment, "date") || "-"}</td>
                <td>
                  <div className="maintenance-comment-text">{getValue(comment, "comment") || "-"}</div>
                  {this.renderParts(getValue(comment, "parts") || [])}
                </td>
                <td>{formatCurrency(getValue(comment, "cost"))}</td>
                <td>{getValue(comment, "updatedBy") || getValue(comment, "createdBy") || "-"}</td>
                <td>{formatDateTime(getValue(comment, "updatedAt") || getValue(comment, "createdAt"))}</td>
                <td>
                  <button type="button" className="maintenance-row-edit-btn" onClick={() => this.openEditComment(comment)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  renderPartEditor = () => {
    const { parts } = this.state.commentForm;
    if (!parts.length) {
      return <div className="maintenance-comment-part-empty">No parts added.</div>;
    }

    return parts.map((part, index) => (
      <div className="maintenance-comment-part-row" key={index}>
        <div className="maintenance-comment-part-grid">
          <label>
            <span>Part Name</span>
            <input value={part.partName} onChange={(event) => this.updatePartRow(index, "partName", event.target.value)} placeholder="e.g. Tyre" />
          </label>
          <label>
            <span>Part Number / SKU</span>
            <input value={part.partNumber} onChange={(event) => this.updatePartRow(index, "partNumber", event.target.value)} />
          </label>
          <label>
            <span>Supplier</span>
            <input value={part.supplier} onChange={(event) => this.updatePartRow(index, "supplier", event.target.value)} />
          </label>
          <label>
            <span>Invoice / Ref</span>
            <input value={part.invoiceNumber} onChange={(event) => this.updatePartRow(index, "invoiceNumber", event.target.value)} />
          </label>
          <label>
            <span>Qty</span>
            <input type="number" min="0" step="1" value={part.quantity} onChange={(event) => this.updatePartRow(index, "quantity", event.target.value)} />
          </label>
          <label>
            <span>Unit Cost</span>
            <input type="number" min="0" step="0.01" value={part.unitCost} onChange={(event) => this.updatePartRow(index, "unitCost", event.target.value)} />
          </label>
          <label>
            <span>Installed Date</span>
            <input type="date" value={part.installedDate} onChange={(event) => this.updatePartRow(index, "installedDate", event.target.value)} />
          </label>
          <label>
            <span>Warranty Months</span>
            <input type="number" min="0" step="1" value={part.warrantyMonths} onChange={(event) => this.updatePartRow(index, "warrantyMonths", event.target.value)} />
          </label>
          <label>
            <span>Warranty Expiry</span>
            <input type="date" value={part.warrantyExpiry} onChange={(event) => this.updatePartRow(index, "warrantyExpiry", event.target.value)} />
          </label>
        </div>
        <label className="maintenance-comment-part-notes">
          <span>Part Notes</span>
          <textarea rows="2" value={part.notes} onChange={(event) => this.updatePartRow(index, "notes", event.target.value)} />
        </label>
        <div className="maintenance-comment-part-actions">
          <button type="button" onClick={() => this.removePartRow(index)}>Remove Part</button>
        </div>
      </div>
    ));
  };

  renderCommentModal = () => {
    if (!this.state.commentModalOpen) return null;
    const { commentForm, editingCommentId, isCommentSaving } = this.state;

    return (
      <div className="maintenance-comment-modal-backdrop" role="presentation">
        <div className="maintenance-comment-modal" role="dialog" aria-modal="true" aria-labelledby="maintenance-comment-modal-title">
          <div className="maintenance-comment-modal-head">
            <h3 id="maintenance-comment-modal-title">{editingCommentId ? "Edit Maintenance Log" : "Add Maintenance Log"}</h3>
            <button type="button" onClick={this.closeCommentModal} disabled={isCommentSaving}>Close</button>
          </div>
          {this.state.commentError && <div className="maintenance-edit-error maintenance-comment-modal-error">{this.state.commentError}</div>}
          <form onSubmit={this.saveComment}>
            <div className="maintenance-comment-form-grid">
              <label>
                <span>Date</span>
                <input type="date" required value={commentForm.date} onChange={(event) => this.handleCommentField("date", event.target.value)} />
              </label>
              <label>
                <span>Cost (AUD)</span>
                <input type="number" min="0" step="0.01" value={commentForm.cost} onChange={(event) => this.handleCommentField("cost", event.target.value)} />
              </label>
            </div>
            <label className="maintenance-comment-textarea-field">
              <span>Log Entry</span>
              <textarea required rows="5" value={commentForm.comment} onChange={(event) => this.handleCommentField("comment", event.target.value)} />
            </label>
            <div className="maintenance-comment-parts-editor">
              <div className="maintenance-comment-parts-head">
                <h4>Parts Used / Warranty</h4>
                <button type="button" onClick={this.addPartRow}>Add Part</button>
              </div>
              {this.renderPartEditor()}
            </div>
            <div className="maintenance-comment-modal-actions">
              {editingCommentId && (
                <button type="button" className="danger" onClick={this.deleteComment} disabled={isCommentSaving}>Delete</button>
              )}
              <button type="button" className="secondary" onClick={this.closeCommentModal} disabled={isCommentSaving}>Cancel</button>
              <button type="submit" disabled={isCommentSaving}>{isCommentSaving ? "Saving..." : "Save"}</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  renderPartsCatalogueModal = () => {
    if (!this.state.partsCatalogueOpen) return null;
    const parts = this.getFilteredPartsCatalogue();

    return (
      <div className="maintenance-report-modal-backdrop" role="presentation">
        <div className="maintenance-report-modal" role="dialog" aria-modal="true" aria-labelledby="maintenance-parts-catalogue-title">
          <div className="maintenance-report-modal-head">
            <div>
              <h3 id="maintenance-parts-catalogue-title">Parts Catalogue</h3>
              <p>{this.getPartsCatalogue().length} part{this.getPartsCatalogue().length === 1 ? "" : "s"} logged for this asset</p>
            </div>
            <button type="button" onClick={() => this.setState({ partsCatalogueOpen: false })}>Close</button>
          </div>
          <div className="maintenance-report-tools">
            <input
              type="search"
              value={this.state.partsCatalogueSearch}
              onChange={(event) => this.setState({ partsCatalogueSearch: event.target.value })}
              placeholder="Search parts catalogue..."
            />
            <button type="button" onClick={() => this.setState({ partsCatalogueSearch: "" })}>Clear</button>
          </div>
          {!parts.length ? (
            <div className="maintenance-report-empty">No parts catalogue entries found for this asset yet.</div>
          ) : (
            <div className="maintenance-report-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Part</th>
                    <th>Supplier</th>
                    <th>Warranty</th>
                    <th>Last Cost</th>
                    <th>Used</th>
                    <th>Last Installed</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((part) => (
                    <tr key={`${part.partName}-${part.partNumber}-${part.supplier}`}>
                      <td>
                        <strong>{part.partName || "-"}</strong>
                        {part.partNumber && <span>{part.partNumber}</span>}
                      </td>
                      <td>{part.supplier || "-"}</td>
                      <td>{part.defaultWarrantyMonths ? `${part.defaultWarrantyMonths} months` : "-"}</td>
                      <td>{formatCurrency(part.lastUnitCost)}</td>
                      <td>
                        <strong>{part.usageCount}</strong>
                        {part.totalQuantity ? <span>Qty {part.totalQuantity}</span> : null}
                      </td>
                      <td>{part.lastInstalledDate || "-"}</td>
                      <td>{part.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  renderWarrantyReportModal = () => {
    if (!this.state.warrantyReportOpen) return null;
    const parts = this.getFilteredWarrantyParts();

    return (
      <div className="maintenance-report-modal-backdrop" role="presentation">
        <div className="maintenance-report-modal wide" role="dialog" aria-modal="true" aria-labelledby="maintenance-warranty-report-title">
          <div className="maintenance-report-modal-head">
            <div>
              <h3 id="maintenance-warranty-report-title">Warranty Report</h3>
              <p>{this.getLoggedParts().length} installed part{this.getLoggedParts().length === 1 ? "" : "s"} recorded for this asset</p>
            </div>
            <button type="button" onClick={() => this.setState({ warrantyReportOpen: false })}>Close</button>
          </div>
          <div className="maintenance-report-tools">
            <input
              type="search"
              value={this.state.warrantyReportSearch}
              onChange={(event) => this.setState({ warrantyReportSearch: event.target.value })}
              placeholder="Search warranty report..."
            />
            <select
              value={this.state.warrantyReportStatus}
              onChange={(event) => this.setState({ warrantyReportStatus: event.target.value })}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring</option>
              <option value="expired">Expired</option>
              <option value="no-expiry">No expiry</option>
            </select>
            <button type="button" onClick={() => this.setState({ warrantyReportSearch: "", warrantyReportStatus: "all" })}>Clear</button>
          </div>
          {!parts.length ? (
            <div className="maintenance-report-empty">No warranty records found for this asset yet.</div>
          ) : (
            <div className="maintenance-report-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Part</th>
                    <th>Supplier</th>
                    <th>Invoice</th>
                    <th>Cost</th>
                    <th>Installed</th>
                    <th>Warranty Ends</th>
                    <th>Status</th>
                    <th>Log Date</th>
                    <th>Log Entry</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((part) => (
                    <tr key={part.id}>
                      <td>
                        <strong>{part.partName || "-"}</strong>
                        {part.partNumber && <span>{part.partNumber}</span>}
                      </td>
                      <td>{part.supplier || "-"}</td>
                      <td>{part.invoiceNumber || "-"}</td>
                      <td>{formatCurrency(part.unitCost)}</td>
                      <td>{part.installedDate || "-"}</td>
                      <td>{part.warrantyExpiry || "-"}</td>
                      <td><span className={`maintenance-warranty-pill ${part.warranty.className}`}>{part.warranty.label}</span></td>
                      <td>{part.logDate || "-"}</td>
                      <td>{part.logEntry || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { asset, form } = this.state;
    const assetName = getValue(asset, "name") || "Maintenance";
    const friendly = getValue(asset, "friendlyName");
    const isTracked = getValue(asset, "isTracked") !== false;
    const serviceKmStatus = isTracked ? this.kmStatus(form.serviceDueKm, getValue(asset, "odometerKm")) : "";
    const filteredComments = this.getFilteredComments();
    const totalCost = filteredComments.reduce((sum, comment) => {
      const cost = Number(getValue(comment, "cost"));
      return sum + (Number.isFinite(cost) ? cost : 0);
    }, 0);

    return (
      <Layout>
        <Helmet>
          <title>Maintenance Details</title>
        </Helmet>
        <div className="contentWrapper contentWrapperSmoothRelease maintenance-edit-page">
          <div className={`maintenance-edit-header${!isTracked ? " non-tracked" : ""}`}>
            <div>
              <h2 className="mainHeading">Asset Details: {assetName}{friendly ? ` (${friendly})` : ""}</h2>
              <p>{isTracked ? `Odometer: ${formatKm(getValue(asset, "odometerKm"))} km` : "Non-tracked asset · no tracker or odometer data"}</p>
              <div className="maintenance-edit-header-meta">
                <span>Customer: <strong>{getValue(asset, "customerName") || "-"}</strong></span>
                <span>Last updated by: <strong>{getValue(asset, "updatedBy") || "-"}</strong></span>
                <span>At: <strong>{formatDateTime(getValue(asset, "updatedAt"))}</strong></span>
              </div>
            </div>
            <div className="maintenance-edit-header-actions">
              <button type="button" className="maintenance-header-action primary" onClick={() => this.setState({ partsCatalogueOpen: true })}>Parts Catalogue</button>
              <button type="button" className="maintenance-header-action primary" onClick={() => this.setState({ warrantyReportOpen: true })}>Warranty Report</button>
              <button type="button" onClick={this.goBack}>Back to Report</button>
            </div>
          </div>

          {this.state.errorMessage && <div className="maintenance-edit-error">{this.state.errorMessage}</div>}
          {this.state.saveMessage && <div className="maintenance-edit-success">{this.state.saveMessage}</div>}

          {this.state.isLoading ? (
            <div className="maintenance-edit-loading">
              <GridLoader size={8} color={"#222222"} loading />
              <span>Loading maintenance details</span>
            </div>
          ) : (
            <div className="maintenance-edit-grid">
              <section className="maintenance-edit-form-panel">
                <div className="maintenance-edit-form-head">
                  <h3>Edit Maintenance</h3>
                  <div className="maintenance-edit-thresholds">
                    <span>Date: {this.state.dateThresholdDays} days</span>
                    {isTracked && <span>KM: {this.state.kmThreshold.toLocaleString()} km</span>}
                  </div>
                </div>
                <form onSubmit={this.save}>
                  {this.renderDateField("registrationDue", "Registration Due")}
                  {this.renderDateField("insuranceDue", "Insurance Due")}
                  {this.renderDateField("roadworthyDue", "Roadworthy Due")}
                  {this.renderDateField("serviceDueDate", "Service Due Date")}
                  {isTracked && <label className={this.fieldClass(serviceKmStatus)}>
                    <span className="maintenance-edit-label-row">
                      <span>Service Due KM</span>
                      {this.renderStatusPill(serviceKmStatus)}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="e.g. 120000"
                      value={form.serviceDueKm}
                      onChange={(event) => this.handleField("serviceDueKm", event.target.value)}
                    />
                  </label>}
                  <div className="maintenance-edit-actions">
                    <button type="button" className="secondary" onClick={this.loadAsset} disabled={this.state.isSaving}>Reset</button>
                    <button type="submit" disabled={this.state.isSaving}>
                      {this.state.isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </section>
              <section className="maintenance-comment-panel">
                <div className="maintenance-comment-head">
                  <h3>Maintenance Log</h3>
                  <div className="maintenance-comment-head-actions">
                    <strong>Total Cost: {formatCurrency(totalCost)}</strong>
                    <button type="button" onClick={this.openNewComment}>Add Log</button>
                  </div>
                </div>
                <div className="maintenance-comment-tools">
                  <input
                    type="search"
                    value={this.state.commentSearch}
                    onChange={(event) => this.setState({ commentSearch: event.target.value })}
                    placeholder="Search maintenance log..."
                  />
                  <button type="button" onClick={() => this.setState({ commentSearch: "" })}>Clear</button>
                </div>
                {this.renderComments()}
              </section>
            </div>
          )}
          {this.renderCommentModal()}
          {this.renderPartsCatalogueModal()}
          {this.renderWarrantyReportModal()}
        </div>
      </Layout>
    );
  }
}

export default MaintenanceEdit;
